/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.14.0
 */

import { ReactNode } from 'react';

import { BalanceDetailsTooltipIcon, Tooltip, Typography } from '@nexign/scc-ui-kit';
import classNames from 'classnames';

import { groupIcon } from '@/main/assets/icons';
import { IAttrStyled } from '@/main/types/globalTypes';

import EllipsisText from '../EllipsisText/EllipsisText';
import { ProgressBarSharedInfoIcon, ProgressBarStyled } from './styled/ProgressBarStyled';

interface IProgressBar extends IAttrStyled {
    // Подсвечивать ли остатки в зависимости от %
    isColorChangedOnValue: boolean;
    labelLeft: string;
    labelRight: ReactNode;
    value: number;
    valueTotal: number;
    valuePercentage?: number;
    unitOfMeasure?: string;
    isShared?: boolean;
    isSpend?: boolean;
    baseVolumeLider?: number;
    availableVolumeLider?: number;
    baseUnitsLider?: string;
    availableUnitsLider?: string;
}

const ProgressBar = ({
    isColorChangedOnValue,
    labelLeft,
    labelRight,
    value,
    valueTotal,
    valuePercentage,
    unitOfMeasure,
    isShared,
    isSpend,
    availableVolumeLider,
    baseVolumeLider,
    baseUnitsLider,
    ...attrStyles
}: IProgressBar) => {
    const w = (valuePercentage && valuePercentage) || value / valueTotal;

    return (
        <ProgressBarStyled {...attrStyles}>
            <div className="progress-bar__title">
                <span className="progress-bar__title-container">
                    <EllipsisText lines={1} title={labelLeft}>
                        {labelLeft}
                    </EllipsisText>

                    {isShared && (
                        <Tooltip
                            arrow
                            body={<Typography variant="body1bold">Объем выделен на группу абонентов</Typography>}
                        >
                            <ProgressBarSharedInfoIcon src={groupIcon} />
                        </Tooltip>
                    )}
                </span>

                <div className="progress-bar__title-right">
                    {labelRight}

                    {Number(baseVolumeLider) > 0 && (
                        <BalanceDetailsTooltipIcon
                            availableVolume={value}
                            availableVolumeLider={availableVolumeLider}
                            baseVolume={valueTotal}
                            baseVolumeLider={baseVolumeLider ?? 0}
                            dateTo=""
                            isShared={isShared}
                            isSpend={isSpend}
                            name=""
                            unitOfMeasure={unitOfMeasure}
                            unitOfMeasureLider={baseUnitsLider}
                        />
                    )}
                </div>
            </div>
            <div className="progress-bar__row">
                <div
                    className={classNames('progress-bar__row-active', {
                        'progress-bar__row-active_success': !isColorChangedOnValue || w > 50,
                        'progress-bar__row-active_warning': isColorChangedOnValue && w > 15 && w <= 50,
                        'progress-bar__row-active_danger': isColorChangedOnValue && w <= 15,
                        'progress-bar__row-active_spend': isSpend,
                    })}
                    style={{ width: w + '%' }}
                />
            </div>
        </ProgressBarStyled>
    );
};

export default ProgressBar;
